import { BUILDINGS, START_HOUSING } from './constants'
import type { Building, Stockpile } from './types'

/** Architectural progression. The town hall always reserves the same seven hexes. */
export type TownHallPhase = 1 | 2 | 3 | 4 | 5

export const TOWN_HALL_PHASES = [
  { phase: 1, name: 'Campsite', description: 'A canvas tent, two bedrolls, and a small fire. A humble place to begin.' },
  { phase: 2, name: 'Settler Camp', description: 'More shelter, stacked logs, and a covered supply area. The camp begins to feel like home.' },
  { phase: 3, name: 'Timber Hall', description: 'A warm timber hall under a thatched roof. Your settlement puts down roots.' },
  { phase: 4, name: 'Fortified Hall', description: 'Stone foundations and timber defenses protect the growing heart of your village.' },
  { phase: 5, name: 'Stone Keep', description: 'A crenellated stone keep with proud banners. From a campfire to a lasting stronghold.' },
] as const satisfies readonly { phase: TownHallPhase; name: string; description: string }[]

/** Saves made before architectural phases start at the campsite too. */
export function townHallPhase(value?: number): TownHallPhase {
  return (Number.isFinite(value) ? Math.max(1, Math.min(5, Math.floor(value!))) : 1) as TownHallPhase
}

export interface TownHallUpgrade {
  phase: TownHallPhase
  population: number
  cost: Partial<Stockpile>
  /** Seconds of village simulation; paused halls pause this timer. */
  duration: number
}

export const TOWN_HALL_UPGRADES: readonly TownHallUpgrade[] = [
  { phase: 2, population: 2, cost: { wood: 500 }, duration: 20 },
  { phase: 3, population: 5, cost: { wood: 140, stone: 60 }, duration: 40 },
  { phase: 4, population: 10, cost: { wood: 220, stone: 180, iron: 20 }, duration: 60 },
  { phase: 5, population: 16, cost: { wood: 300, stone: 360, iron: 60, gold: 40 }, duration: 90 },
]

export function nextTownHallUpgrade(phase?: number): TownHallUpgrade | undefined {
  return TOWN_HALL_UPGRADES.find(upgrade => upgrade.phase === townHallPhase(phase) + 1)
}

const BONUSES = [
  { hp: 0, storage: 0, housing: 0 },
  { hp: 200, storage: 200, housing: 2 },
  { hp: 500, storage: 500, housing: 4 },
  { hp: 900, storage: 900, housing: 7 },
  { hp: 1600, storage: 1500, housing: 10 },
] as const

/** Cumulative bonuses leave phase-one and old-save rules unchanged. */
export function townHallBonuses(phase?: number): Readonly<{ hp: number; storage: number; housing: number }> {
  return BONUSES[townHallPhase(phase) - 1]
}

type HallStats = Pick<Building, 'type' | 'hallPhase'>

export function buildingMaxHp(b: HallStats): number {
  return BUILDINGS[b.type].hp + (b.type === 'townhall' ? townHallBonuses(b.hallPhase).hp : 0)
}

export function buildingStorage(b: HallStats): number {
  return (BUILDINGS[b.type].storage ?? 0) + (b.type === 'townhall' ? townHallBonuses(b.hallPhase).storage : 0)
}

export function buildingHousing(b: HallStats, startingPair = false): number {
  if (b.type !== 'townhall') return BUILDINGS[b.type].housing ?? 0
  return (startingPair ? START_HOUSING : BUILDINGS.townhall.housing ?? 0) + townHallBonuses(b.hallPhase).housing
}
